import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Grid, TextField, Button, Stack, Avatar, Alert, Divider, CircularProgress,
} from '@mui/material';
import {
  ArrowBack, PersonOutlined, EmailOutlined, PhoneOutlined, BusinessOutlined, Edit, Check,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { C } from '../vps/theme';
import { getUser, updateUser } from '../../services/userService';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, login, logout } = useAuth();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(!!user?.id);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phoneNumber || '',
    company: 'Hosting Services Client',
  });

  // Load the latest profile info from the backend whenever we have a logged-in user id
  useEffect(() => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    let isMounted = true;
    const fetchProfile = async () => {
      setLoading(true);
      setErrorMsg('');
      try {
        const data = await getUser(user.id);
        if (!isMounted) return;
        setFormData((prev) => ({
          ...prev,
          name: data?.name ?? prev.name,
          email: data?.email ?? prev.email,
          phone: data?.phoneNumber ?? prev.phone,
        }));
      } catch (err) {
        if (isMounted) setErrorMsg(err.message || 'Unable to load profile details.');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchProfile();
    return () => { isMounted = false; };
  }, [user?.id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleEditToggle = () => {
    setErrorMsg('');
    setSuccessMsg('');
    setEditing(true);
  };

  const handleSave = async () => {
    if (!user?.id) {
      setErrorMsg('Your session is missing an account ID. Please log out and sign in again.');
      return;
    }
    if (!formData.name.trim()) {
      setErrorMsg('Full name cannot be empty.');
      return;
    }

    setSaving(true);
    setErrorMsg('');
    try {
      await updateUser({
        id: user.id,
        name: formData.name.trim(),
        phoneNumber: formData.phone.trim(),
      });

      // Keep the AuthContext / localStorage user object in sync with what we just saved
      login({ ...user, name: formData.name.trim(), phoneNumber: formData.phone.trim() });

      setEditing(false);
      setSuccessMsg('Profile information updated successfully!');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err) {
      setErrorMsg(err.message || 'Unable to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleRelogin = () => {
    logout();
    navigate('/login');
  };

  if (!user) {
    return (
      <Box sx={{ background: C.bgSection, minHeight: '90vh', py: { xs: 4, md: 8 } }}>
        <Container maxWidth="sm">
          <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, borderRadius: '16px', background: '#FFFFFF', border: `1px solid ${C.border}`, textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: C.textPrimary, mb: 1.5 }}>
              You're not signed in
            </Typography>
            <Typography sx={{ color: C.textMuted, mb: 3 }}>
              Please sign in to view and manage your profile.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate('/login')}
              sx={{ background: C.primary, color: '#FFFFFF', fontWeight: 700, borderRadius: '8px', textTransform: 'none', boxShadow: 'none', '&:hover': { background: C.primaryHover } }}
            >
              Go to Login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ background: C.bgSection, minHeight: '90vh', py: { xs: 4, md: 8 } }}>
      <Container maxWidth="md">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ color: C.primary, fontWeight: 600, textTransform: 'none', mb: 3, '&:hover': { background: C.primaryLight } }}
        >
          Back to Home
        </Button>

        <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, borderRadius: '16px', background: '#FFFFFF', border: `1px solid ${C.border}` }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems="center" sx={{ mb: 4 }}>
            <Avatar sx={{ width: 72, height: 72, background: C.primary, fontSize: '1.8rem', fontWeight: 800 }}>
              {(formData.name || 'U')[0].toUpperCase()}
            </Avatar>
            <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h4" sx={{ fontWeight: 800, color: C.textPrimary, mb: 0.5 }}>
                {formData.name || 'Your Profile'}
              </Typography>
              <Typography variant="body2" sx={{ color: C.textMuted }}>
                {formData.email} · Client Account
              </Typography>
            </Box>
            <Button
              variant={editing ? 'contained' : 'outlined'}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : editing ? <Check /> : <Edit />}
              disabled={saving || loading}
              onClick={editing ? handleSave : handleEditToggle}
              sx={{
                background: editing ? C.primary : 'transparent',
                borderColor: C.primary,
                color: editing ? '#FFFFFF' : C.primary,
                fontWeight: 700, borderRadius: '8px', textTransform: 'none',
                '&:hover': { background: editing ? C.primaryHover : C.primaryLight },
              }}
            >
              {saving ? 'Saving...' : editing ? 'Save Changes' : 'Edit Profile'}
            </Button>
          </Stack>

          {errorMsg && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: '10px' }}
              action={
                !user?.id ? (
                  <Button color="inherit" size="small" onClick={handleRelogin} sx={{ fontWeight: 700, textTransform: 'none' }}>
                    Log out &amp; sign in
                  </Button>
                ) : undefined
              }
            >
              {errorMsg}
            </Alert>
          )}
          {successMsg && <Alert severity="success" sx={{ mb: 3, borderRadius: '10px' }}>{successMsg}</Alert>}

          <Divider sx={{ borderColor: C.border, mb: 4 }} />

          {loading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : (
            // MUI v6 Grid size
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Full Name" name="name" value={formData.name} onChange={handleChange} disabled={!editing} InputProps={{ startAdornment: <PersonOutlined sx={{ color: C.textMuted, mr: 1 }} /> }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Email Address" name="email" value={formData.email} disabled InputProps={{ startAdornment: <EmailOutlined sx={{ color: C.textMuted, mr: 1 }} /> }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Phone / WhatsApp" name="phone" value={formData.phone} onChange={handleChange} disabled={!editing} InputProps={{ startAdornment: <PhoneOutlined sx={{ color: C.textMuted, mr: 1 }} /> }} />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth label="Company / Organization" name="company" value={formData.company} onChange={handleChange} disabled={!editing} InputProps={{ startAdornment: <BusinessOutlined sx={{ color: C.textMuted, mr: 1 }} /> }} />
              </Grid>
            </Grid>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ProfilePage;
